import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  Fragment,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import ReportPageHeader from "src/components/pages/Reports/Report/ReportPageHeader";
import Loader from "src/components/shared/Loader/Loader";
import { getOrigin } from "src/environment";
import { useGetApi, usePostApi } from "src/hooks";
import { createPdfHead } from "src/mock/createPdfHead";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  resetReportData,
  setReportData,
} from "src/redux/slices/entriesGeneratorSlice";
import { convertFromUUIDKeys } from "src/redux/utils/convertFromUUIDKeys";
import { insertValues } from "src/redux/utils/insertValues";
import { PageContainer } from "src/styled";
import { Notification } from "src/utils/Notification";

import TimeInputPopup from "../../../components/shared/TimeInput/TimeInputPopup";
import type { Stamp } from "../../../interfaces/Stamps";
import { IReportEntry, type IReportData } from "./Report.types";
import { getPdfBody } from "./Report.utils";
import { resolveSignatures } from "./resolveSignatures";
import { getWidgetSections } from "src/components/pages/Home/CustomDashboard/Widget.utils";

const ReportResult = lazy(
  () => import("src/components/pages/Reports/Report/Result"),
);

const Report = () => {
  const methods = useForm();
  const [isFileLoading, setIsFileLoading] = useState({
    pdf: false,
    spreadsheet: false,
    csv: false,
  });

  // Set during PDF export so the virtualized entry list renders in full.
  const [renderAllEntries, setRenderAllEntries] = useState(false);

  const { reportId } = useParams();

  const dispatch = useDispatch();

  const sections = useSelector(state => state.generatorSlice.lbentry.sections);

  useEffect(() => {
    return () => {
      dispatch(resetReportData());
    };
  }, [dispatch]);

  const mapReportData = useCallback(
    (response: any) => {
      const data = { ...response, data: resolveSignatures(response?.data) };
      if (
        Array.isArray(data?.data?.result) &&
        data?.data?.result?.some((e: any) => e.weid)
      )
        return {
          ...data.data,
          result: data.data.result.map((e: any) => ({
            ...e,
            deletedRows: e.deletedRows.map((r: any) => ({
              ...r,
              sections: getWidgetSections(r.sections),
            })),
            history: e.history.map((h: any) => ({
              ...h,
              lbentry: { sections: getWidgetSections(h.lbentry.sections) },
            })),
            lbentry: { sections: getWidgetSections(e.lbentry.sections) },
          })),
        };
      dispatch(setReportData(data.data));
      return data.data as IReportData;
    },
    [dispatch],
  );

  const { data, setData, isLoading, refetch } = useGetApi({
    request: "run-report",
    body: {
      params: { rid: reportId },
    },
    mapper: mapReportData,
  });
  const { data: stamp, refetch: fetchStamp } = useGetApi<{ stamp: Stamp }>({
    request: "get-stamp",
    fetchOnInit: false,
  });

  const { post } = usePostApi();

  const ref = useRef<HTMLDivElement>(null);

  const handleError = useCallback(() => {
    Notification(
      "Error",
      "Something went wrong. Try to re-run the report or try again later",
      "red",
    );
  }, []);

  const prepareMutableReport = useCallback(() => {
    const values = methods.getValues();
    const modifiedLogbook = insertValues(values, sections);
    const originalLogbookVersion = convertFromUUIDKeys(modifiedLogbook);
    setData?.(prev =>
      prev
        ? {
            ...prev,

            lbdata: { ...prev.lbdata, sections: originalLogbookVersion },
          }
        : undefined,
    );

    return originalLogbookVersion;
  }, [methods, sections, setData, data]);

  const handlePdf = useCallback(
    async (pdfMode: "entries" | "charts" = "entries") => {
      if (!data || !ref.current) return;
      if (data.report.type === 3) {
        prepareMutableReport();
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      if (data.report.type === 5 && "syncFormValues" in ref.current) {
        (ref.current as any).syncFormValues();
      }

      // getPdfBody copies the DOM, so mount every entry and let it lay out first.
      const needsFullRender = pdfMode !== "charts";
      if (needsFullRender) {
        flushSync(() => setRenderAllEntries(true));
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      try {
        const body = await getPdfBody(ref.current, pdfMode);
        const pdfParams: any = {
          head: createPdfHead,
          name: data?.data?.name
            ? data?.data?.name
            : data?.report?.name
              ? data?.report?.name
              : "Report",
          body,
          rid: reportId,
        };

        // Add A4 dimensions in inches for HTML reports that need portrait page sizing
        if (data.report.type === 5 || data.isHTML || pdfMode === "charts") {
          pdfParams.width = 8.27; // A4 width in inches
          pdfParams.height = 11.69; // A4 height in inches
        }

        const fileData = await post({
          request: data.report.type === 3 ? "create-wd-pdf" : "create-pdf",
          body: {
            params: pdfParams,
          },
        });

        if (window.api) {
          window.api.downloadFile(
            fileData.file,
            ({ full, path }) => {
              notifications.show({
                title: "Report Downloaded",
                message: (
                  <div>
                    <Button
                      variant="subtle"
                      compact
                      onClick={() => {
                        if (window.api) window.api.openFile(full);
                      }}>
                      Open File
                    </Button>
                    <Button
                      variant="subtle"
                      compact
                      onClick={() => {
                        if (window.api) window.api.openFile(path);
                      }}>
                      Open Folder
                    </Button>
                  </div>
                ),
              });
            },
            "reports",
          );
        } else {
          window.open(fileData.file, "_blank");
        }
      } catch {
        handleError();
      } finally {
        setRenderAllEntries(false);
        setIsFileLoading({
          pdf: false,
          spreadsheet: false,
          csv: false,
        });
      }
    },
    [data, handleError, post, prepareMutableReport, stamp],
  );

  const handleSpreadSheet = useCallback(
    async (csv: boolean) => {
      if (!data || !ref.current) return;

      let sig = null;
      let mutableSections: any[] = [];

      if (data.signature?.data) sig = data.signature.data;

      if (data.report.type === 3) {
        mutableSections = prepareMutableReport();
        const img = ref.current?.querySelector(
          "#signature-img",
        ) as HTMLImageElement;
        if (img?.src) sig = img.src;
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      let result = data.result as IReportEntry[];

      if (
        data.report.type !== 3 &&
        data.report.type !== 1 &&
        typeof data.result !== "string"
      ) {
        // parse sub rows if needed for normal reports
        result = data.result
          ? data.result.map(entry => {
              const sections = entry.lbentry.sections
                ? entry.lbentry.sections.map(section => {
                    if (section.subSections) return section;
                    return {
                      ...section,
                      headers: Array.isArray(section.headers[0])
                        ? section.headers[0]
                        : section.headers,
                      rows:
                        (section.rows as any)[0]?.[0] &&
                        Array.isArray((section.rows as any)[0][0])
                          ? section.rows.flat()
                          : section.rows,
                    };
                  })
                : [];
              return {
                ...entry,
                lbentry: { ...entry.lbentry, sections },
              };
            })
          : [];
      }
      if (
        data.report.type === 8 ||
        data.report.type === 9 ||
        data.report.type === 6
      ) {
        result = data.data;
      }
      try {
        const fileData = await post({
          request: "create-spreadsheet",
          body: {
            params: {
              csv,
              name: data.data.name
                ? data.data.name
                : data.report?.name
                  ? data.report.name
                  : "Report",
              rep: data.report,
              data:
                data.report.type === 3
                  ? { ...data.lbdata, sections: mutableSections }
                  : result,
              sig,
            },
          },
        });
        const url = getOrigin();
        const filepath = url + "/" + fileData.file;
        if (window.api) {
          window.api.downloadFile(
            filepath,
            ({ full, path }) => {
              notifications.show({
                title: "Report Downloaded",
                message: (
                  <div>
                    <Button
                      variant="subtle"
                      compact
                      onClick={() => {
                        if (window.api) window.api.openFile(full);
                      }}>
                      Open File
                    </Button>
                    <Button
                      variant="subtle"
                      compact
                      onClick={() => {
                        if (window.api) window.api.openFile(path);
                      }}>
                      Open Folder
                    </Button>
                  </div>
                ),
              });
            },
            "reports",
          );
        } else {
          window.open(filepath, "_blank");
        }
      } catch {
        handleError();
      } finally {
        setIsFileLoading({
          pdf: false,
          spreadsheet: false,
          csv: false,
        });
      }
    },
    [data, handleError, post, prepareMutableReport],
  );

  const handleGenerate = useCallback(
    async (
      type: "pdf" | "spreadsheet",
      csv = false,
      pdfMode: "entries" | "charts" = "entries",
    ) => {
      if (data.isHTML || data.report.type === 5) {
        if (typeof data?.result !== "string" || type !== "pdf") return;
      }
      if (ref.current && data) {
        setIsFileLoading({
          pdf: type === "pdf",
          spreadsheet: type === "spreadsheet" && !csv,
          csv: type === "spreadsheet" && csv,
        });

        if (type === "pdf") {
          await handlePdf(pdfMode);
        } else if (type === "spreadsheet") {
          await handleSpreadSheet(csv);
        }
      } else {
        handleError();
      }
    },
    [data, handleError, handlePdf, handleSpreadSheet],
  );

  const fetchRestricted = useCallback(
    (token: string) => {
      void refetch({ params: { tid: token } });
    },
    [refetch],
  );

  return (
    <Fragment>
      <PageContainer>
        <ReportPageHeader
          data={data}
          handleGenerate={handleGenerate}
          isFileLoading={isFileLoading}
          fetchRestricted={fetchRestricted}
          isHtmlString={data?.isHTML || typeof data?.result === "string"}
        />
        <FormProvider {...methods}>
          {isLoading ? (
            <Loader />
          ) : (
            <Suspense fallback={<Loader />}>
              <ReportResult
                ref={ref}
                data={data}
                renderAllEntries={renderAllEntries}
              />
            </Suspense>
          )}
        </FormProvider>
      </PageContainer>
      <TimeInputPopup />
    </Fragment>
  );
};

export default Report;
